import { readCookie } from './readCookie';

// OneTrust's scripts set this global variable after they load.
declare global {
  interface Window {
    OnetrustActiveGroups?: string;
  }
}

// These cookie category codes are provided by OneTrust
// and map onto corresponding category names.
export type CookieCategoryCode = 'C0001' | 'C0002' | 'C0004';

export type CookieCategoryName = 'essential' | 'analytics' | 'marketing';

type CategoryNameToCodeMap = {
  [key in CookieCategoryName]: CookieCategoryCode;
};

type ActiveCategoriesByName = {
  [key in CookieCategoryName]: boolean;
};

const categoryCodes: Array<CookieCategoryCode> = ['C0001', 'C0002', 'C0004'];

const categoryNameToCodeMap: CategoryNameToCodeMap = {
  essential: 'C0001',
  analytics: 'C0002',
  marketing: 'C0004',
};

type CategoryCodeToNameMap = {
  [key in CookieCategoryCode]: CookieCategoryName;
};

type ActiveCategoriesByCode = {
  [key in CookieCategoryCode]: boolean;
};

const categoryCodeToNameMap = Object.entries(categoryNameToCodeMap).reduce(
  (accum, entry) => {
    // Didn't destructure these because using `as` worked better for typing.
    const name = entry[0] as CookieCategoryName;
    const code = entry[1] as CookieCategoryCode;
    accum[code] = name;
    return accum;
  },
  {} as CategoryCodeToNameMap
);

// Read consent values from `OptanonConsent` cookie. This is used as
// a fallback for when the `OnetrustActiveGroups` global variable
// doesn't exist yet because the scripts that create it haven't
// finished loading and executing. The benefit of using the `OptanonConsent`
// cookie as a fallback is that it's available immediately on page
// load once it's been set at least once. This means that on subsequent
// page loads the consented categories can be read even when `OnetrustActiveGroups`
// doesn't exist yet, which is useful for some app-level code that only
// executes a single time on boot and therefore can't react to a hook
// like `useCookieConsent` which would occur long after the app's initial boot.
const readCookieCategoriesFromCookie = () => {
  const optanonConsent = readCookie('OptanonConsent');
  const groups = new URLSearchParams(optanonConsent).get('groups') || '';
  const activeCodes = groups
    .split(',')
    .map((group) => group.split(':'))
    // Filter out non-existent groups, which occurs when `OptanonConsent`
    // hasn't yet been set.
    .filter(([groupCode]) => !!groupCode)
    // Filter out falsey groups that the user hasn't consented to.
    .filter(([, isConsented]) => isConsented === '1')
    .map(([groupCode]) => groupCode);
  return activeCodes;
};

// Name of the DOM event that the cookie-consent script (in the sibling
// `@zapier/cookie-consent` package) dispatches on `window` whenever the
// user's consent state resolves or changes. Lifted into a shared constant
// so `writeCookie` and `useCookieConsent` can't drift apart.
export const COOKIE_CONSENT_EVENT = 'zapier.cookie-consent';

// Parse the comma-delimited `OnetrustActiveGroups` global into its active
// category codes. OneTrust delimits with commas, so a not-yet-initialized
// value like `,,` parses to an empty list even though the raw string is
// truthy. Shared by `isConsentReady` and `getActiveCategoryCodes` so the
// readiness check and the category read interpret the global the same way.
const parseActiveGroupCodes = (raw: string): Array<string> =>
  raw.split(/\s*,\s*/).filter(Boolean);

// True when the consent decision can be answered now: either OneTrust has
// populated `OnetrustActiveGroups` with at least one real category code, or the
// persisted `OptanonConsent` cookie encodes a real decision in its `groups`
// segment. False during the OneTrust loading window on first-page visits, when
// emitting would produce NULL visitor_id values in page_view events.
//
// Both paths require at least one real category code; neither a truthy-but-empty
// global nor a merely-present cookie counts. OneTrust sets `OnetrustActiveGroups`
// to a transient placeholder (an empty string, or commas with nothing between
// them like `,,`) before consent resolves, and `OTSDKstub.js` can write an
// `OptanonConsent` cookie with empty/absent `groups` before the decision lands;
// both parse to zero codes here. A resolved decision always carries at least the
// always-on `C0001`, so requiring a parsed code distinguishes resolved from
// placeholder on both paths and keeps callers waiting for the real consent
// signal. This is a consent-decision check, not an SDK-loaded check.
export const isConsentReady = (): boolean => {
  // Match the SSR guard in `waitForConsentReady` so server renders don't
  // log a noisy `console.error` on every initial mount of `useCookieConsent`.
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    if (parseActiveGroupCodes(window.OnetrustActiveGroups || '').length > 0) {
      return true;
    }
  } catch (error) {
    console.error('Failed to access window.OnetrustActiveGroups:', error);
    return false;
  }
  // `readCookieCategoriesFromCookie` keeps only groups flagged `:1`. A resolved
  // decision always includes the strictly-necessary `C0001:1` (it cannot be
  // declined), so a non-empty result means consent resolved and an empty one is
  // the pre-decision placeholder. If a partial cookie ever lacked `C0001:1` we
  // read not-ready and fall back to the `zapier.cookie-consent` event, so the
  // assumption fails safe rather than dropping a visitor_id.
  return readCookieCategoriesFromCookie().length > 0;
};

export const getActiveCategoryCodes = () => {
  let activeGroups = '';

  try {
    activeGroups = window.OnetrustActiveGroups || '';
  } catch (error) {
    console.error('Failed to access window.OnetrustActiveGroups:', error);
  }

  // "Live" codes from the `OnetrustActiveGroups` global variable.
  const liveActiveCodes = parseActiveGroupCodes(activeGroups);
  // See note at `readCookieCategoriesFromCookie` definition.
  const activeCodes =
    liveActiveCodes.length > 0
      ? liveActiveCodes
      : readCookieCategoriesFromCookie();
  // Use `Set` to dedupe array, being sure that C0001 (essential category)
  // always exists within it.
  return [
    ...new Set(['C0001', ...activeCodes]),
  ].sort() as Array<CookieCategoryCode>;
};

export const readCookieCategories = () => {
  const activeCodes = getActiveCategoryCodes();
  const byCode: ActiveCategoriesByCode = categoryCodes.reduce(
    (accum, catCode) => {
      accum[catCode] = activeCodes.includes(catCode);
      return accum;
    },
    {} as ActiveCategoriesByCode
  );
  const byName: ActiveCategoriesByName = Object.entries(byCode).reduce(
    (accum, [categoryCode, isActive]) => {
      const categoryName =
        categoryCodeToNameMap[categoryCode as CookieCategoryCode];
      accum[categoryName] = isActive;
      return accum;
    },
    {} as ActiveCategoriesByName
  );
  return { byCode, byName };
};

// Indicates whether the cookie categorized as `category` can be
// written based on user consent.
export const canWriteCookieCategory = (category: CookieCategoryName) => {
  // In case `OnetrustActiveGroups` hasn't been set yet, still allow
  // essential cookies to be set.
  if (category === 'essential') {
    return true;
  }
  const cookieCategories = readCookieCategories();
  return !!cookieCategories.byName[category];
};
